import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/articles/${slug}`)
      .then((r) => { setArticle(r.data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="animate-pulse space-y-4 max-w-screen-md w-full px-6 py-20">
          <div className="h-4 bg-[#E7E5E4] rounded w-1/4" />
          <div className="h-8 bg-[#E7E5E4] rounded w-3/4" />
          <div className="h-8 bg-[#E7E5E4] rounded w-1/2" />
        </div>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex flex-col items-center justify-center text-center px-6">
        <h1 className="font-bold text-2xl text-[#1C1C1C] mb-3">Article not found</h1>
        <p className="text-[#57534E] mb-6">This article may have moved or doesn't exist.</p>
        <Link to="/articles" className="bg-[#1B4332] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#2D6A4F] transition-colors">
          Browse all articles
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Breadcrumb */}
      <div className="border-b border-[#E7E5E4] bg-white px-6 py-4">
        <div className="max-w-screen-md mx-auto">
          <Link
            to="/articles"
            className="inline-flex items-center gap-1.5 text-sm text-[#57534E] hover:text-[#1B4332] transition-colors"
            data-testid="back-to-articles"
          >
            <ArrowLeft size={14} />
            All articles
          </Link>
        </div>
      </div>

      {/* Article header */}
      <header className="py-12 px-6 bg-white border-b border-[#E7E5E4]">
        <div className="max-w-screen-md mx-auto space-y-4">
          <div className="flex items-center gap-3">
            <Link
              to={`/topics/${article.topic?.toLowerCase().replace(/\s/g, "-")}`}
              className="topic-badge hover:text-[#2D6A4F] transition-colors"
            >
              {article.topic}
            </Link>
            <span className="text-[#E7E5E4]">·</span>
            <span className="flex items-center gap-1 text-xs text-[#A8A29E]">
              <Clock size={11} />
              {article.read_time}
            </span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C1C1C] leading-tight tracking-tight">
            {article.title}
          </h1>
          <p className="text-[#57534E] text-lg leading-relaxed">{article.excerpt}</p>
          <div className="flex items-center gap-3 pt-2">
            <div className="w-8 h-8 bg-[#1B4332] rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">M</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#1C1C1C]">Michael</p>
              <p className="text-xs text-[#A8A29E]">
                {new Date(article.created_at).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" })}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Article content */}
      <article className="py-12 px-6">
        <div className="max-w-screen-md mx-auto">
          <div
            className="article-content prose text-[#1C1C1C]"
            dangerouslySetInnerHTML={{ __html: article.content }}
            data-testid="article-content"
          />

          {/* Bottom CTA */}
          <div className="mt-16 border-t border-[#E7E5E4] pt-10">
            <div className="bg-[#F2EFE9] rounded-xl p-8 text-center space-y-4">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373]">Get more like this</p>
              <h3 className="font-bold text-xl text-[#1C1C1C]">
                One careful email. Twice a month.
              </h3>
              <p className="text-[#57534E] text-sm max-w-sm mx-auto">
                The best new article, one Canadian money idea, and a practical tip — straight to your inbox.
              </p>
              <Link
                to="/"
                className="inline-block bg-[#1B4332] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#2D6A4F] transition-colors"
                data-testid="article-subscribe-cta"
              >
                Subscribe to the newsletter
              </Link>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
